import xml2js from 'xml2js';

import { BusinessDocType } from '../../types/Enums';
import type { WorkflowViewModel } from '../../services/backendServices/ViewModels';

const EMPTY_SENTINEL_VALUES = new Set(['NaN', 'null', 'undefined']);

/** Same labels as `SortedSelect` enum options: string enum key per numeric value. */
const numericEnumToAuditDbIdMappings = (
    enumObject: Record<string, string | number>
): { ID: string; IDMapping: string }[] =>
    Object.keys(enumObject)
        .filter((key) => Number.isNaN(Number(key)))
        .map((key) => {
            const val = enumObject[key];
            if (typeof val !== 'number') {
                return null;
            }
            return { ID: String(val), IDMapping: key };
        })
        .filter((row): row is { ID: string; IDMapping: string } => row !== null);

export const buyerCustomJSRuleMappingAuditBusinessDocTypeDbIdMappings = numericEnumToAuditDbIdMappings(
    BusinessDocType as unknown as Record<string, string | number>
);

export const workflowViewModelsToWorkflowAuditDbIdMappings = (
    workflows: WorkflowViewModel[]
): { ID: string; IDMapping: string }[] => workflows.map((w) => ({ ID: String(w.Id), IDMapping: w.Name }));

export const workflowViewModelsToWorkflowActivityAuditDbIdMappings = (
    workflows: WorkflowViewModel[]
): { ID: string; IDMapping: string }[] => {
    const rows: { ID: string; IDMapping: string }[] = [];
    for (const w of workflows) {
        for (const a of w.WorkflowActivities ?? []) {
            rows.push({ ID: String(a.Id), IDMapping: a.Name || `Activity ${a.Id}` });
        }
    }
    return rows;
};

/**
 * Parse audit XML for `AllowedExecutionContexts` (ExecutionContext/Context entries).
 */
export const parseBuyerCustomJSRuleMappingAuditInputParameterOverridesXml = (
    value: string,
    keyToLabelMap: Record<string, string>
): Map<string, string> => {
    const parser = new xml2js.Parser();
    const parsedResultMap: Map<string, string> = new Map();

    parser.parseString(value, (err, result) => {
        if (!err && result) {
            const parameters: Array<Record<string, string[]>> = result?.InputParameters?.Parameters?.[0].Parameter;
            if (!parameters) {
                return;
            }
            parameters.forEach((parameter) => {
                const parameterName = parameter.Name?.[0] ?? '';
                if (!parameterName) {
                    return;
                }
                Object.entries(parameter).forEach(([key, val]) => {
                    // skip Name key
                    if (key === 'Name') {
                        return;
                    }
                    // append parameter name to the key
                    if (!keyToLabelMap[key]) {
                        return;
                    }
                    const displayLabel = `${parameterName}->${keyToLabelMap[key]}`;

                    let rawValue = val?.[0] ?? '';
                    if (typeof rawValue === 'object') {
                        rawValue = JSON.stringify(rawValue);
                    }
                    const parameterValue = EMPTY_SENTINEL_VALUES.has(rawValue) ? '' : rawValue;
                    if (!parameterValue) {
                        return;
                    }
                    parsedResultMap.set(displayLabel, parameterValue);
                });
            });
        }
    });

    return parsedResultMap;
};

/**
 * Parse audit XML for `AllowedExecutionContexts` (ExecutionContext/Context entries).
 */
export const parseBuyerCustomJSRuleMappingAuditAllowedExecutionContextsXml = (
    value: string,
    keyToLabelMap: Record<string, string>
): Map<string, string> => {
    const parser = new xml2js.Parser();
    const parsedResultMap: Map<string, string> = new Map();

    parser.parseString(value, (err, result) => {
        if (!err && result) {
            const parameters: Array<Record<string, string[]>> = result?.ExecutionContext?.Context;
            if (!parameters) {
                return;
            }
            parameters.forEach((parameter) => {
                Object.entries(parameter).forEach(([key, val]) => {
                    const displayLabel = keyToLabelMap[key];
                    if (!displayLabel) {
                        return;
                    }

                    let rawValue = val?.[0] ?? '';
                    if (typeof rawValue === 'object') {
                        rawValue = JSON.stringify(rawValue);
                    }
                    const parameterValue = EMPTY_SENTINEL_VALUES.has(rawValue) ? '' : rawValue;
                    if (!parameterValue) {
                        return;
                    }

                    parsedResultMap.set(displayLabel, parameterValue);
                });
            });
        }
    });

    return parsedResultMap;
};
